import analytics from 'yoda-core-components/lib/helpers/Analytics/Analytics';
import { QUICKSCREEN_ANALYTICS_EVENT } from 'yoda-account-components/lib/actionTypes/SYFActionTypes';
import BaseActionFilter from 'yoda-core-components/lib/helpers/Analytics/BaseActionFilter';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import { getFeatureflag } from 'yoda-core-components/lib/helpers/Utils/Utils';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

/**
 * Moved from Account components
 */
import { getModuleTrackingPageType } from '../helpers/Utils/utils';
import { logwebvitals } from './WebvitalsMiddleware';
import * as SigninLoadedActionTypes from '../actionTypes/SigninActionTypes';
import * as CreateAccountActionTypes from '../actionTypes/CreateAccountActionTypes';
import * as PasswordResetActionTypes from '../actionTypes/PasswordResetActionTypes';
import * as RewardsLoadedActionTypes from '../actionTypes/RewardsActionTypes';

// Phone no login & verification analytics
import {
    POST_REQUEST_OTP_SUCCESS,
    POST_REQUEST_OTP_ERROR,
    POST_VERIFY_OTP_SUCCESS,
    POST_VERIFY_OTP_ERROR,
    STOP_OTP_REQUEST,
    START_PHONE_VERIFY,
} from '../actionTypes/PhoneVerificationActionTypes';

import SigninActionFilter from '../analytics/SigninActionFilter';
import CreateAccountActionFilter from '../analytics/CreateAccountActionFilter';
import ForgotPasswordActionFilter from '../analytics/ForgotPasswordActionFilter';
import RewardsActionFilter from '../analytics/RewardsActionFilter';
import PhoneVerificationActionFilter from '../analytics/PhoneVerificationActionFilter';
import PDPAnalyticsActionFilter from '../analytics/PDPAnalyticsActionFilter';
import { getFitFinderDigitalData } from '../analytics/InteractiveVizNavHelper';

import AnalyticsActionTypes from '../actionTypes/AnalyticsActionTypes';
import {
    CREATE_SITE_LIST_CLICK_EVENT,
    SITE_MOVETOLIST_ERROR_EVENT,
    ADD_SITE_TO_LIST_ERROR_EVENT,
    ADD_SITE_TO_LIST_EVENT,
    MOVE_SITE_ITEMS_EVENT_ANALYTICS,
} from '../actionTypes/SavedListToolTip';
import addToCartActionTypes from '../actionTypes/AddToCartActionTypes';
import { ERROR_PAGE_LOADED_ANALYTICS, SHOW_ERROR_PAGE } from '../actionTypes/ErrorPageActionTypes';
import { IRIS_LOAD_ERROR_PAGE } from '../actionTypes/IrisActionTypes';
import { CM_LOAD_ERROR_PAGE } from '../actionTypes/CMPageActionTypes';
import { UPDATE_ITEM_LIST_SLIDER } from '../actionTypes/ListsActionTypes';

const baseActionFilter = new BaseActionFilter();
const signinActionFilter = new SigninActionFilter();
const createAccActionFilter = new CreateAccountActionFilter();
const forgotPasswordActionFilter = new ForgotPasswordActionFilter();
const rewardsActionFilter = new RewardsActionFilter();
const phoneVerificationActionFilter = new PhoneVerificationActionFilter();
const analyticsActionFilter = new PDPAnalyticsActionFilter();
/* istanbul ignore next */
if (!__SERVER__) {
    const { hostname } = window.location;
    const allowedHostnames = ['www.jcpenney.com', 'assistive.jcpenney.com', 'preview.jcpenney.com'];

    const enableAdobeLaunch = getFeatureflag('enableAdobeLaunch');
    if (enableAdobeLaunch) {
        let adobeLaunchUrl =
            '//assets.adobedtm.com/bd23ab4f15ae/265606982074/launch-8455c60e4f1f-development.min.js';

        if (allowedHostnames.includes(hostname)) {
            adobeLaunchUrl =
                '//assets.adobedtm.com/bd23ab4f15ae/265606982074/launch-333d6a4b1735.min.js';
        }

        const config = {
            adobeLaunchUrl,
        };
        analytics.initLaunch(config);
    }

    const config = {
        jqueryUrl: '//code.jquery.com/jquery-3.4.1.min.js',
    };

    analytics.init(config);
}

const getErrorPageData = (store, errorStatus) => {
    const state = store.getState();
    const { deviceType, __RELEASEVERSION__ } = state.context;
    const { isDesktop } = deviceType;
    const device = isDesktop ? 'desktop' : deviceType.isMobile ? 'mobile' : 'tablet';

    const error = [
        {
            errorDescription: `${errorStatus} error`,
        },
    ];

    return {
        page: {
            pageInfo: {
                pageType: 'error',
                pageName: `${errorStatus} error`,
                siteSection: `${errorStatus} error`,
                deviceType: device,
                releaseVersion: __RELEASEVERSION__,
                // eslint-disable-line
                version: 'yoda error 1',
            },
        },
        error,
    };
};

/**
 * This function will used only for redux middleware only to track tagging based on action request
 */
const CommonMiddleware = (store) => (next) => (action) => {
    // eslint-disable-line
    if (!__SERVER__) {
        let digitalData = null;
        let analyticsEventName = null;

        const handleAppBannerAction = (action) => {
            switch (action.type) {
                case AnalyticsActionTypes.APP_BANNER_OPEN:
                    return { digitalData: {}, analyticsEventName: 'branchioImpression' };
                case AnalyticsActionTypes.APP_BANNER_CLOSE:
                    return { digitalData: {}, analyticsEventName: 'branchioClosure' };
                default:
                    return { digitalData: null, analyticsEventName: null };
            }
        };

        const handleFormErrorAction = (action) => {
            const { fraudForceErrData = undefined } = action?.errorDetails;
            const pageInfo = action?.pageInfo;
            delete action?.errorDetails?.fraudForceErrData;
            let digitalData = baseActionFilter.getFormErrorDL(action.errorDetails);
            digitalData = {
                ...digitalData,
                ...(fraudForceErrData || {}),
                ...(isEmpty(pageInfo) ? {} : pageInfo),
            };
            return { digitalData, analyticsEventName: 'formError' };
        };

        const handleFormErrorActionOOSSponsoredProduct = (action) => {
            const digitalData = { error: action?.errorDetails };
            return { digitalData, analyticsEventName: 'formError' };
        };

        const handleNavigationClickAction = (action) => {
            const digitalData = baseActionFilter.getNavigationDL(action.navClick);
            const analyticsEventName = digitalData ? 'navigationClick' : null;
            return { digitalData, analyticsEventName };
        };

        /**
         * component can trigger an action only for tagging
         * action should contain analytics object as mandatory
         * @param analytics.type - {string}  - tagging key
         * @param analytics.JCPDL - {Object}  - required additional details for tagging event
         */
        try {
            switch (action.type) {
                case AnalyticsActionTypes.APP_BANNER_OPEN:
                case AnalyticsActionTypes.APP_BANNER_CLOSE:
                    return handleAppBannerAction(action);
                case AnalyticsActionTypes.FORM_ERROR: {
                    const data = handleFormErrorAction(action);
                    digitalData = data.digitalData;
                    analyticsEventName = data.analyticsEventName;
                    break;
                }
                case AnalyticsActionTypes.OOS_SPONSORED_PRODUCTFORM_ERROR: {
                    const data = handleFormErrorActionOOSSponsoredProduct(action);
                    digitalData = data.digitalData;
                    analyticsEventName = data.analyticsEventName;
                    break;
                }
                case AnalyticsActionTypes.NAVIGATION_CLICK: {
                    const data = handleNavigationClickAction(action);
                    digitalData = data.digitalData;
                    analyticsEventName = data.analyticsEventName;
                    break;
                }
                case AnalyticsActionTypes.ANALYTICS_STORE_SEARCH:
                    if (action.payload && action.payload.address) {
                        digitalData = baseActionFilter.storeSearchData(action.payload);
                        analyticsEventName = 'storeSearch';
                    }
                    break;
                case AnalyticsActionTypes.ANALYTICS_VIDEO_LOADED:
                    digitalData = baseActionFilter.videoLoadedData(action.payload);
                    analyticsEventName = action.payload.eventName;
                    break;

                case AnalyticsActionTypes.ANALYTICS_OPEN_MODAL:
                    digitalData = baseActionFilter.openModelData(action.name);
                    analyticsEventName = 'openModal';
                    break;
                case MOVE_SITE_ITEMS_EVENT_ANALYTICS:
                    digitalData = {};
                    analyticsEventName = 'moveItemBetweenList';
                    break;

                case CREATE_SITE_LIST_CLICK_EVENT:
                    digitalData = {};
                    analyticsEventName = 'createNewList';
                    break;

                case ADD_SITE_TO_LIST_EVENT:
                    digitalData = action.analyticsData || action.payload;
                    analyticsEventName = 'saveProduct';
                    break;

                case AnalyticsActionTypes.FILTERS_BY_PILLS_ANALYTICS:
                    digitalData = { servicesSelected: action.payload };
                    analyticsEventName = 'findAStoreFilterByService';
                    break;

                case UPDATE_ITEM_LIST_SLIDER:
                    digitalData = {};
                    analyticsEventName = 'updateItemListSlider';
                    break;

                case SITE_MOVETOLIST_ERROR_EVENT:
                    digitalData = { error: action.payload };
                    analyticsEventName = 'formError';
                    break;

                case ADD_SITE_TO_LIST_ERROR_EVENT:
                    digitalData = action.errorData;
                    analyticsEventName = 'formError';
                    break;
                case addToCartActionTypes.MYLIST_CART_ADD_ANALYTICS:
                    digitalData = action.analyticsData;
                    analyticsEventName = 'cartAdd';
                    break;
                case ERROR_PAGE_LOADED_ANALYTICS:
                    digitalData = getErrorPageData(store, action.payload.status);
                    digitalData = baseActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case SigninLoadedActionTypes.SIGNIN_PAGE_LOADED_SUCCESS:
                    // console.log('********* CkMiddleware SIGNIN_PAGE_LOADED_SUCCESS ***********');
                    digitalData = signinActionFilter.getSigninPageDL(store, action.payload);
                    digitalData = signinActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    logwebvitals(store, digitalData);
                    break;
                case SigninLoadedActionTypes.GROUPB_PAGE_LOADED_SUCCESS:
                    // console.log('********* CkMiddleware GROUPB_PAGE_LOADED_SUCCESS ***********');
                    digitalData = signinActionFilter.getGroupBPL(store);
                    digitalData = signinActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case SigninLoadedActionTypes.SIGNIN_FORM_ERROR:
                    // console.log('********* CkMiddleware SIGNIN_FORM_ERROR ***********');
                    digitalData = signinActionFilter.getSigninPageFormErrorPL(action.payload);
                    analyticsEventName = 'formError';
                    break;
                case SigninLoadedActionTypes.POST_SIGNIN_REQUEST_ERROR_ANALYTICS:
                    // console.log('********* CkMiddleware POST_SIGNIN_REQUEST_ERROR_ANALYTICS ***********');
                    digitalData = signinActionFilter.getSigninPageFormErrorPL(action.payload);
                    analyticsEventName = 'formError';
                    break;
                case SigninLoadedActionTypes.SIGNIN_ATTEMPTED:
                    // console.log('********* CkMiddleware SIGNIN_ATTEMPTED ***********');
                    digitalData = {};
                    analyticsEventName = 'loginAttempted';
                    break;
                case SigninLoadedActionTypes.SIGNIN_FAILED:
                    // console.log('********* CkMiddleware SIGNIN_FAILED ***********');
                    digitalData = {};
                    /* istanbul ignore next */
                    if (action.isPhone) {
                        analyticsEventName = 'loginFailedTrigger';
                    } else {
                        analyticsEventName = 'loginFailed';
                    }
                    break;
                case SigninLoadedActionTypes.SIGNIN_SUCCESS:
                    // console.log('********* CkMiddleware SIGNIN_SUCCESS ***********');
                    digitalData = {
                        keepMeLoggedIn: get(action, 'keepMeLoggedIn') ? 'yes' : 'no',
                        // rememberMe: get(action, 'rememberMe') ? 'yes' : 'no', // Can be used for remember-me if required
                    };
                    /* istanbul ignore next */
                    if (action.isPhone) {
                        analyticsEventName = 'loginSuccessTrigger';
                    } else {
                        analyticsEventName = 'loginSuccess';
                    }
                    break;
                case CreateAccountActionTypes.CREATE_ACCOUNT_PAGE_LOADED_SUCCESS:
                    // console.log('********* CkMiddleware CREATE_ACCOUNT_PAGE_LOADED_SUCCESS ***********');
                    digitalData = createAccActionFilter.getCreateAccountPageDL(
                        store,
                        action.payload
                    );
                    digitalData = createAccActionFilter.updateCommonDigitalData(digitalData);
                    logwebvitals(store, digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case CreateAccountActionTypes.SIGNOUT_PAGE_LOAD_ACTION:
                    // console.log('********* CkMiddleware CREATE_ACCOUNT_PAGE_LOADED_SUCCESS ***********');
                    digitalData = createAccActionFilter.getSignoutPageDL(store, action.payload);
                    digitalData = createAccActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case CreateAccountActionTypes.GROUPA_PAGE_LOADED_SUCCESS:
                    // console.log('********* CkMiddleware GROUPA_PAGE_LOADED_SUCCESS ***********');
                    digitalData = createAccActionFilter.getGroupAPL(store);
                    digitalData = createAccActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case CreateAccountActionTypes.ACCOUNT_CREATION_ERROR:
                    // console.log('********* CkMiddleware ACCOUNT_CREATION_ERROR ***********');
                    digitalData = createAccActionFilter.getCreateAccountFormErrorPL(action.payload);
                    analyticsEventName = 'formError';
                    break;
                case CreateAccountActionTypes.ACCOUNT_REGISTERED:
                    // console.log('********* CkMiddleware ACCOUNT_REGISTERED ***********');
                    digitalData = createAccActionFilter.getAccountRegisteredPL();
                    analyticsEventName = 'accountRegistration';
                    break;
                case PasswordResetActionTypes.FORGOT_PASSWORD_ANALYTICS:
                    // console.log('********* CkMiddleware FORGOT_PASSWORD_ANALYTICS ***********');
                    digitalData = forgotPasswordActionFilter.getForgotPasswordDL(store);
                    digitalData = forgotPasswordActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'forgotPassword';
                    break;
                case PasswordResetActionTypes.GET_ACCOUNT_EMAIL_SUCCESS:
                    // console.log('********* CkMiddleware GET_ACCOUNT_EMAIL_SUCCESS ***********');
                    digitalData = forgotPasswordActionFilter.getAccountEmailSuccess(store);
                    digitalData = forgotPasswordActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case PasswordResetActionTypes.GET_ACCOUNT_EMAIL_ERROR:
                    // console.log('********* CkMiddleware GET_ACCOUNT_EMAIL_ERROR ***********');
                    digitalData = forgotPasswordActionFilter.getAccountEmailError(
                        store,
                        action.errorResponse || {}
                    );
                    digitalData = forgotPasswordActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'digitalDataReady';
                    break;
                case PasswordResetActionTypes.PASSWORD_SET_FORM_ERROR:
                    // console.log('********* CkMiddleware PASSWORD_SET_FORM_ERROR ***********');
                    // console.log('PASSWORD_SET_FORM_ERROR', action);
                    digitalData = forgotPasswordActionFilter.getSetPasswordFormError(
                        action.payload
                    );
                    // digitalData = forgotPasswordActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'formError';
                    break;
                case PasswordResetActionTypes.PASSWORD_SET_ERROR:
                    // console.log('PASSWORD_SET_ERROR', action);
                    // console.log('********* CkMiddleware PASSWORD_SET_FORM_ERROR ***********');
                    digitalData = forgotPasswordActionFilter.getSetPasswordServiceError(
                        action.payload
                    );
                    digitalData = forgotPasswordActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = 'formError';
                    break;
                case PasswordResetActionTypes.PASSWORD_SET_SUCCESS:
                    // console.info('PASSWORD_SET_SUCCESS action', action);
                    // console.log('********* CkMiddleware PASSWORD_SET_SUCCESS ***********');
                    digitalData = {};
                    analyticsEventName = 'resetPassword';
                    break;
                case PasswordResetActionTypes.FORGOT_PASSWORD_SUCCESS:
                    // console.log('********* CkMiddleware FORGOT_PASSWORD_SUCCESS ***********');
                    digitalData = {};
                    analyticsEventName = 'forgotPassword';
                    break;
                case RewardsLoadedActionTypes.REWARDS_MIGRATION_SUCCESS:
                    // console.log('********* CkMiddleware REWARDS_MIGRATION_SUCCESS ***********');
                    digitalData = rewardsActionFilter.getLinkRewardsAccountPL(action.payload);
                    // digitalData = accountActionFilter.updateCommonDigitalData(digitalData);
                    analyticsEventName = action.payload.trackEvent;
                    break;

                /* Phone verification analytics starts here */

                case POST_REQUEST_OTP_ERROR: {
                    const serviceError = get(action, 'payload', {});
                    digitalData = phoneVerificationActionFilter.getPhoneVerificationFormErrorDL(
                        serviceError
                    );
                    analyticsEventName = 'requestOTPFailureTrigger';
                    break;
                }
                case POST_VERIFY_OTP_ERROR: {
                    const serviceError = get(action, 'payload', {});
                    digitalData = phoneVerificationActionFilter.getPhoneVerificationFormErrorDL(
                        serviceError
                    );
                    analyticsEventName = 'verifyOTPFailureTrigger';
                    break;
                }
                case POST_REQUEST_OTP_SUCCESS: {
                    digitalData = {};
                    analyticsEventName = 'requestOTPSuccessTrigger';
                    break;
                }
                case POST_VERIFY_OTP_SUCCESS: {
                    digitalData = {};
                    analyticsEventName = 'verifyOTPSuccessTrigger';
                    break;
                }
                case STOP_OTP_REQUEST: {
                    const { isSkipTrigger } = action;
                    if (isSkipTrigger) {
                        digitalData = {};
                        analyticsEventName = 'phoneVerificationSkip';
                    }
                    break;
                }
                case START_PHONE_VERIFY: {
                    digitalData = phoneVerificationActionFilter.getPhoneVerificationPageLoad(store);
                    digitalData = phoneVerificationActionFilter.updateCommonDigitalData(
                        digitalData
                    );
                    analyticsEventName = 'digitalDataReady';
                    break;
                }
                /* Phone verification analytics ends here */
                /* Password reset by phone analytics starts here */
                case PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_ERROR:
                case PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD_ERROR:
                case PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_SUCCESS:
                case PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD_SUCCESS:
                case PasswordResetActionTypes.PASSWORD_RESET_BY_PHONE_INITIATED:
                case PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER_SUCCESS:
                case PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER_ERROR:
                    digitalData = {};
                    analyticsEventName = get(action, 'eventName', '');
                    break;
                /* Password reset by phone analytics ends here */
                case AnalyticsActionTypes.LOG_CUSTOM_ERRORS:
                    digitalData = baseActionFilter.getNonCustomerFacingErrors(action.errorDetails);
                    analyticsEventName = 'triggerEnsightenError';
                    break;

                case AnalyticsActionTypes.PRODUCT_RECOMMENDATIONS_ANALYTICS:
                    /* istanbul ignore next */
                    if (
                        action.payload &&
                        action.payload.productStrategy &&
                        getFeatureflag('enableProdRecommendationAnalytics')
                    ) {
                        digitalData = baseActionFilter.captureRecommendationsScheme(action.payload);
                        analyticsEventName = 'productRecommendationStrategy';
                    }
                    break;

                case AnalyticsActionTypes.TRIGGER_BOPIS_MODAL: {
                    digitalData = {};
                    analyticsEventName = 'bopisHeaderModal';
                    break;
                }

                case AnalyticsActionTypes.TRIGGER_BOPIS_PICKUP_STORE_CHANGE: {
                    digitalData = {};
                    analyticsEventName = 'bopisPickupStoreChange';
                    break;
                }

                case AnalyticsActionTypes.TRIGGER_BOPIS_DEFAULT_STORE_CHANGE: {
                    digitalData = {};
                    analyticsEventName = 'bopisDefaultStoreChange';
                    break;
                }

                case AnalyticsActionTypes.TRIGGER_SKINCARE_MODAL: {
                    digitalData = {};
                    analyticsEventName = 'skinCareAdvisor';
                    break;
                }

                case AnalyticsActionTypes.TRIGGER_VTO_MODAL: {
                    digitalData = {};
                    analyticsEventName = 'vto';
                    break;
                }

                case AnalyticsActionTypes.TRIGGER_HAIRCARE_MODAL: {
                    digitalData = {};
                    analyticsEventName = 'hairCareAdvisor';
                    break;
                }

                case AnalyticsActionTypes.TRIGGER_REVIEVE_ADDTOCART_ANALYTICS: {
                    const productinfo = get(action, 'payload', {});
                    digitalData = {
                        product: productinfo && productinfo.data,
                    };
                    analyticsEventName = 'cartAdd';
                    break;
                }

                case IRIS_LOAD_ERROR_PAGE:
                case CM_LOAD_ERROR_PAGE: {
                    const { statusCode } = action;
                    const payload = { status: statusCode };
                    /* istanbul ignore next */
                    if (action.showErrorPage) {
                        store.dispatch({
                            type: SHOW_ERROR_PAGE,
                            payload,
                        });
                    }
                    break;
                }
                case AnalyticsActionTypes.BREADCRUMB_ANALYTICS: {
                    /* istanbul ignore next */
                    const breadcrumbData = Cookies.load('breadCrumbClickDigitalData', true);
                    /* istanbul ignore next */
                    if (breadcrumbData) Cookies.remove('navigationClickDigitalData');
                    digitalData = baseActionFilter.loadBreadCrumbData(action.breadcrumbData);
                    analyticsEventName = digitalData ? 'breadcrumbData' : null;
                    break;
                }

                case AnalyticsActionTypes.FITFINDER_MODAL_AND_PERSONA: {
                    const {
                        page: { attributes: { fitFinderProgramName, fitFinderPersona } = {} } = {},
                    } = getFitFinderDigitalData(store) || {};
                    digitalData = {
                        fitFinderProgramName,
                        fitFinderPersona,
                    };
                    analyticsEventName = 'fitFinderComponents';
                    break;
                }

                /* istanbul ignore next */
                case AnalyticsActionTypes.TRIGGER_PAGE_READY:
                    digitalData = store.getState().digitalData;
                    analyticsEventName = 'digitalDataReady';
                    break;
                case AnalyticsActionTypes.TRIGGER_ACTIVE_CART_REMINDER:
                    digitalData = {};
                    analyticsEventName = 'activeCartReminder';
                    break;
                case AnalyticsActionTypes.PLCC_APPLY_ANALYTICS:
                    digitalData = action.payload;
                    analyticsEventName = 'plccapplynowlink';
                    break;
                case AnalyticsActionTypes.PLCC_VIEWS_ANALYTICS:
                    digitalData = action.payload;
                    analyticsEventName = 'plcccreditupsellviews';
                    break;
                case AnalyticsActionTypes.ANALYTICS_SUCCESSFUL_SUBSCRIPTION:
                    if (action?.payload?.optinType) {
                        digitalData = baseActionFilter.successfulSubscriptionData(action.payload);
                        analyticsEventName = 'updateNotificationOptins';
                    }
                    break;
                case AnalyticsActionTypes.ADI_SITE_PROBE_SUCCESS_ANALYTICS:
                    digitalData = action.payload?.data;
                    analyticsEventName = action.payload?.eventName;
                    break;
                case AnalyticsActionTypes.REPORT_WEB_VITALS:
                    const stateObj = store.getState();
                    const disableTrackWebVitals =
                        stateObj?.context?.featureFlags?.disableTrackWebVitals;
                    if (!disableTrackWebVitals) logwebvitals(store);
                    break;
                case AnalyticsActionTypes.PDP_ALL_SKU_SELECTED:
                    digitalData = analyticsActionFilter.getSelectSkuPDP(action.payload);
                    analyticsEventName = 'selectSkuPDP';
                    break;
                case QUICKSCREEN_ANALYTICS_EVENT:
                    digitalData = action.payload;
                    analyticsEventName = 'eSyncQuickScreen';
                    break;
                case AnalyticsActionTypes.BNPL_ANALYTICS_EVENT:
                    digitalData = action.payload;
                    analyticsEventName = 'PayLaterSynchrony';
                    break;
                case AnalyticsActionTypes.PLCC_ANALYTICS_EVENT:
                case AnalyticsActionTypes.TRIGGER_MODULE_IMPRESSION_TRACKING:
                    digitalData = {
                        module: {
                            locationpath:
                                action?.payload?.locationpath || window?.location?.pathname,
                            linkposition: action?.payload?.linkposition || 0,
                            targetpath: action?.payload?.targetpath || '',
                            ...action?.payload,
                            name: `${getModuleTrackingPageType(window?.location?.pathname)} : ${
                                action?.payload?.name
                            }`,
                        },
                    };
                    let eventName;
                    if (action.type === AnalyticsActionTypes.TRIGGER_MODULE_IMPRESSION_TRACKING) {
                        eventName = 'moduleimpressiontracking';
                    } else {
                        eventName = 'moduletracking';
                    }
                    analyticsEventName = eventName;
                    break;
                default:
                    break;
            }

            if (analyticsEventName || digitalData) {
                analytics.track(analyticsEventName, digitalData);
            }

            if (!action.navClick) {
                const pendingNavigationClickData = baseActionFilter.getPendingNavigationDL();
                /* istanbul ignore else */
                if (pendingNavigationClickData && !isEmpty(window.digitalData)) {
                    analytics.track('navigationClick', pendingNavigationClickData);
                    Cookies.remove('navigationClickDigitalData');
                }
            }
        } catch (error) {
            // @TODO - LOGGER should add here
        }
    } else {
        try {
            const handleLoadErrorPageAction = (store, action) => {
                const { statusCode, showErrorPage } = action;
                if (
                    (action.type === IRIS_LOAD_ERROR_PAGE || action.type === CM_LOAD_ERROR_PAGE) &&
                    showErrorPage
                ) {
                    const payload = { status: statusCode };
                    store.dispatch({
                        type: SHOW_ERROR_PAGE,
                        payload,
                    });
                }
            };

            const handleAction = (store, action) => {
                switch (action.type) {
                    // Handle IRIS_LOAD_ERROR_PAGE and CM_LOAD_ERROR_PAGE actions
                    case IRIS_LOAD_ERROR_PAGE:
                    case CM_LOAD_ERROR_PAGE:
                        handleLoadErrorPageAction(store, action);
                        break;
                    // Handle other action types...
                    default:
                        break;
                }
            };

            // Usage example:
            handleAction(store, action);
        } catch (error) {
            // @TODO - LOGGER should add here
        }
    }
    return next(action);
};

export default CommonMiddleware;
